import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
  BuilderKind,
  BuilderLayoutConfig,
  BuilderLayoutValidation,
} from './self-service-config.types';

/**
 * The subset of a shell config a per-chart file may contribute. A chart file is
 * NOT a full BuilderLayoutConfig — it only carries its own `layoutSteps` entries
 * and `options` fragments, which the loader deep-merges into the shell.
 */
interface PartialChartConfig {
  chart?: string;
  layoutSteps?: Record<string, unknown>;
  options?: Record<string, unknown>;
}

/**
 * Loads and validates the JSON layout configs that drive the self-service
 * builders (see JSON_CONFIG_PLAN.md). Each builder fetches its layout from
 * `assets/self-service-config/<builder>-builder.json`; when the file is absent
 * or invalid the service returns the builder's built-in default layout so the
 * UI renders exactly as it does today.
 *
 * Mirrors the shared ConfigService.getDashboardConfig pattern (static asset
 * fetch under /assets), kept local to the feature to avoid coupling the
 * self-service layer to the dashboard config service.
 */
@Injectable({ providedIn: 'root' })
export class SelfServiceConfigService {
  private readonly http = inject(HttpClient);

  private static readonly CONFIG_BASE = '/assets/self-service-config';
  private static readonly SUPPORTED_MAJOR = 1;

  /**
   * Load a builder's layout config. Falls back to `defaultLayout` on any
   * failure (missing file, network error, malformed JSON) so the builder always
   * has a usable layout. Validation issues are surfaced via `validateLayout`,
   * which callers may run separately to toast warnings; load itself never throws.
   */
  loadBuilderLayout(
    builder: BuilderKind,
    defaultLayout: BuilderLayoutConfig,
  ): Observable<BuilderLayoutConfig> {
    const url = `${SelfServiceConfigService.CONFIG_BASE}/${builder}-builder.json`;
    return this.http.get<BuilderLayoutConfig>(url).pipe(
      map((config) => this.coerceLayout(config, builder, defaultLayout)),
      switchMap((shell) => this.mergeChartConfigs(shell)),
      catchError(() => of(defaultLayout)),
    );
  }

  /**
   * Load each per-chart config file named in `shell.chartConfigs` and deep-merge
   * it into the shell. Each chart file lives at `charts/<name>.json` and carries
   * only its own `layoutSteps` + `options` fragments. A missing/invalid file is
   * skipped (it merges as empty), so a chart file never blanks the builder.
   */
  private mergeChartConfigs(shell: BuilderLayoutConfig): Observable<BuilderLayoutConfig> {
    const names = Array.isArray(shell.chartConfigs) ? shell.chartConfigs.filter((n) => !!n) : [];
    if (names.length === 0) return of(shell);

    const fetches = names.map((name) =>
      this.http
        .get<PartialChartConfig>(`${SelfServiceConfigService.CONFIG_BASE}/charts/${name}.json`)
        .pipe(catchError(() => of<PartialChartConfig>({}))),
    );

    return forkJoin(fetches).pipe(
      map((charts) => charts.reduce((acc, chart) => this.applyChartConfig(acc, chart), shell)),
      catchError(() => of(shell)),
    );
  }

  /** Fold one chart file into the shell: layoutSteps by key, options per-key. */
  private applyChartConfig(shell: BuilderLayoutConfig, chart: PartialChartConfig): BuilderLayoutConfig {
    if (!chart || typeof chart !== 'object') return shell;
    return {
      ...shell,
      layoutSteps: { ...(shell.layoutSteps ?? {}), ...(chart.layoutSteps ?? {}) } as BuilderLayoutConfig['layoutSteps'],
      options: this.mergeOptions(
        (shell.options ?? {}) as Record<string, unknown>,
        (chart.options ?? {}) as Record<string, unknown>,
      ) as BuilderLayoutConfig['options'],
    };
  }

  /**
   * Per-key merge of two `options` blocks. For each key in the incoming block:
   * arrays are CONCATENATED onto the shell's (so e.g. formatControls accumulate),
   * plain objects are shallow-merged, and scalars/absent keys are set. Comment
   * keys (`// ...`) are ignored. The shell's own value wins only when the chart
   * doesn't supply the key.
   */
  private mergeOptions(
    base: Record<string, unknown>,
    incoming: Record<string, unknown>,
  ): Record<string, unknown> {
    const out: Record<string, unknown> = { ...base };
    for (const [key, value] of Object.entries(incoming)) {
      if (key.startsWith('//')) continue;
      const existing = out[key];
      if (Array.isArray(value)) {
        out[key] = Array.isArray(existing) ? [...existing, ...value] : [...value];
      } else if (value && typeof value === 'object' && existing && typeof existing === 'object' && !Array.isArray(existing)) {
        out[key] = { ...(existing as Record<string, unknown>), ...(value as Record<string, unknown>) };
      } else {
        out[key] = value;
      }
    }
    return out;
  }

  /**
   * Validate a layout config against the builder's known field-id registry.
   * Pure: callers pass the set of registry ids the builder exposes.
   */
  validateLayout(
    config: BuilderLayoutConfig,
    knownFieldIds: ReadonlySet<string>,
  ): BuilderLayoutValidation {
    const errors: string[] = [];
    const unknownFields: string[] = [];

    if (!config || typeof config !== 'object') {
      return { valid: false, unknownFields, errors: ['Config is empty or not an object.'] };
    }

    const major = this.majorVersion(config.version);
    if (major === null) {
      errors.push(`Missing or unparseable version "${config.version}".`);
    } else if (major !== SelfServiceConfigService.SUPPORTED_MAJOR) {
      errors.push(
        `Unsupported config major version ${major} (expected ${SelfServiceConfigService.SUPPORTED_MAJOR}).`,
      );
    }

    if (!Array.isArray(config.steps) || config.steps.length === 0) {
      errors.push('Config has no steps.');
    } else {
      for (const step of config.steps) {
        for (const section of step.sections ?? []) {
          for (const field of section.fields ?? []) {
            if (field?.field && !knownFieldIds.has(field.field)) {
              unknownFields.push(field.field);
            }
          }
        }
      }
    }

    return {
      valid: errors.length === 0 && unknownFields.length === 0,
      unknownFields: Array.from(new Set(unknownFields)),
      errors,
    };
  }

  /**
   * Accept the loaded config only if it is structurally usable (right builder,
   * supported major, non-empty steps). Otherwise fall back to the default so a
   * partially-written or stale file can never blank out a builder.
   */
  private coerceLayout(
    config: BuilderLayoutConfig,
    builder: BuilderKind,
    defaultLayout: BuilderLayoutConfig,
  ): BuilderLayoutConfig {
    const major = this.majorVersion(config?.version);
    const usable =
      !!config &&
      config.builder === builder &&
      major === SelfServiceConfigService.SUPPORTED_MAJOR &&
      Array.isArray(config.steps) &&
      config.steps.length > 0;
    return usable ? config : defaultLayout;
  }

  private majorVersion(version: string | undefined): number | null {
    if (!version) return null;
    const major = parseInt(String(version).split('.')[0], 10);
    return Number.isFinite(major) ? major : null;
  }
}
